<template>
  <div
    class="c-grid-view"
    @click.self="clearSelection"
  >
    <div class="c-grid-view__controls mb-3">
      <b-button-group>
        <b-button
          v-if="!isViewMode"
          variant="outline-primary"
          size="sm"
          :disabled="isLoading"
          @click="toggleSelectedOnly"
        >
          <i :class="['fas', 'mr-1', !selectedOnly ? 'fa-square' : 'fa-check-square']" />
          View Selected
        </b-button>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="toggleSelectAll"
        >
          <i :class="['fas', 'mr-1', selectedItems.length ? 'fa-square' : 'fa-check-square']" />
          {{ selectedItems.length ? 'Unselect All' : 'Select All' }}
        </b-button>
      </b-button-group>
    </div>

    <div
      v-show="folderItems.length"
      class="c-grid-view__container folder-items pb-3"
      @click.self="clearSelection"
    >
      <GridItem
        v-for="folderItem in folderItems"
        :key="folderItem.id"
        :item="folderItem"
        :is-selected="isSelected(folderItem)"
        :is-available-overlay="false"
        :draggable="isDragAndDropAvailable"
        @click="itemClickHandled"
        @dblclick="dbClickHandler"
        @dragstart="onDragstart($event, folderItem)"
        @drop.stop="onDrop($event, folderItem)"
      />
    </div>
 
    <div
      class="c-grid-view__container other-items"
      @click.self="clearSelection"
    >
      <GridItem
        v-for="otherItem in otherItems"
        :key="otherItem.id"
        :item="otherItem"
        :is-selected="isSelected(otherItem)"
        :is-available-overlay="isItemOverlayEnabled"
        :draggable="isDragAndDropAvailable"
        @click="itemClickHandled"
        @dblclick="dbClickHandler"
        @dragstart="onDragstart($event, otherItem)"
        @drop.stop="onDrop($event, otherItem)"
      />
      <GridItemAdd
        v-if="showItemAdd"
        v-b-tooltip.hover
        title="Add more product images"
        @click="addItemHandle"
        @dblclick="addItemHandle"
      />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import GridItem from "../GridItem.vue";
import {MUTATIONS} from "@frontend/store/file-browser-product-selector";
import {FILE_TYPE} from "@frontend/constants/file-image-types";
import GridItemAdd from "@frontend/components/modules/file-browser-product-selector/browser/GridItemAdd.vue";
import {MODES} from "@frontend/components/modules/file-browser-product-selector/constants";

const { mapActions, mapGetters, mapMutations } = createNamespacedHelpers('fileBrowserProductSelector')

export default {
  name: "GridView",
  components: {GridItem, GridItemAdd},
  props: {
    isDragAndDropAvailable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    dragIcon: null,
    selectedOnly: false,
    isLoading: false,
  }),
  computed: {
    ...mapGetters([
      'items',
      'isSelected',
      'onlyOneItemSelection',
      'selectedItems',
      'isItemOverlayEnabled',
      'isItemsPickEnabled',
      'currentFolderId',
      'mode',
      'isMoveProductModeEnabled',
      'isItemPicked'
    ]),
    folderItems() {
      return this.items.filter(item => item.type === FILE_TYPE.FOLDER);
    },
    otherItems() {
        return this.items.filter(item => item.type !== FILE_TYPE.FOLDER && item.full_url).sort((a, b) => {
        //apply sort only for selectedItems POR-9122
        if (!a.id || !b.id || !String(a.id || '').startsWith('image_') || !String(b.id || '').startsWith('image_')) return 0;
        
        return a.id.split('_').pop() - b.id.split('_').pop();
      });
    },
    showItemAdd() {
      if (this.isMoveProductModeEnabled) {
        return false;
      }
      return this.mode === MODES.VIEW && !this.currentFolderId;
    },
    isViewMode() {
      return this.mode === MODES.VIEW;
    }
  },
  mounted() {
    const dragIcon = new Image();
    dragIcon.src = "/img/icons/image-drag-icon.svg";

    this.dragIcon = dragIcon;
  },
  methods: {
    ...mapActions([
      'clearSelection', 
      'setCurrentFolder', 
      'togglePickedItem', 
      'dropProductFile',
      'enableBrowserMode',
      'pickItem',
      'fetchData',
      'setAddItemsContext'
    ]),
    ...mapMutations([
      MUTATIONS.TOGGLE_SELECTED_ITEMS, 
      MUTATIONS.CLEAR_SELECTION, 
      MUTATIONS.SET_MODAL,
      MUTATIONS.SET_SHOW_ONLY_SELECTED,
    ]),
    itemClickHandled(item) {
      if (item.name == '...') {
        return
      }
      if (this.mode !== MODES.BROWSER) {
        this.clearSelection();
      }
      if (this.isItemsPickEnabled &&
        item.type !== FILE_TYPE.FOLDER &&
        (this.onlyOneItemSelection || (item.count === 1 || item.count === 0))) {
        this.togglePickedItem(item);
      }
      this[MUTATIONS.TOGGLE_SELECTED_ITEMS](item);
      
      //there's no need to pick folder
      if (this.isItemPicked(item) || item.type === FILE_TYPE.FOLDER || item?.element_id) return;
      
      this.pickItem({
        ...item,
        count: item.count + 1
      });
    },
    dbClickHandler(item) {
      if (item.type === FILE_TYPE.FOLDER) {
        this.setCurrentFolder(item);
      }
    },
    onDragstart(event, item) {
      if (!this.isDragAndDropAvailable) return;
      this.draggedItem = item;

      if (item.type !== FILE_TYPE.FOLDER) {
        event.dataTransfer.setDragImage(this.dragIcon, 64, 64);
      }
    },
    async onDrop(event, itemToDropIn) {
      if (itemToDropIn.type === 'folder') {
        let itemsForSending = [];

        if (this.selectedItems.some(selectedItem => selectedItem.id === this.draggedItem.id)) {
          itemsForSending = this.selectedItems;
        } else {
          itemsForSending = [this.draggedItem];
        }

        itemsForSending = itemsForSending.filter(itemForSending => itemForSending.id !== itemToDropIn.id)
        
        for (const item of itemsForSending) {
          await this.dropProductFile({ id: item.id, parent_id: itemToDropIn.id, type: item.type });
        }
      }
    },
    addItemHandle() {
      this[MUTATIONS.CLEAR_SELECTION]();
      this.enableBrowserMode();
      this.setAddItemsContext(true);
    },
    selectAllItems() {
      const itemsToSelect = [...this.folderItems, ...this.otherItems];
      this[MUTATIONS.CLEAR_SELECTION]();
      itemsToSelect.forEach(item => {
        if (item.name !== '...') {
          this[MUTATIONS.TOGGLE_SELECTED_ITEMS](item);
          if (this.isItemsPickEnabled && 
              item.type !== FILE_TYPE.FOLDER && 
              (this.onlyOneItemSelection || (item.count === 1 || item.count === 0))) {
            this.togglePickedItem(item);
          }
        }
      });
    },
    unselectAllItems() {
      this.selectedItems.forEach(item => {
        if (this.isItemsPickEnabled && 
            item.type !== FILE_TYPE.FOLDER && 
            (this.onlyOneItemSelection || (item.count === 1 || item.count === 0))) {
          this.togglePickedItem(item);
        }
      });
      this[MUTATIONS.CLEAR_SELECTION]();
    },
    toggleSelectAll() {
      if (this.selectedItems.length) {
        this.unselectAllItems();
      } else {
        this.selectAllItems();
      }
    },
    async toggleSelectedOnly() {
      this.isLoading = true;
      if (this.selectedOnly) {
        this.selectedOnly = !this.selectedOnly;
        this[MUTATIONS.SET_SHOW_ONLY_SELECTED](false);
        await this.fetchData();
        this.isLoading = false;
        return;
      }
      
      this.selectedOnly = !this.selectedOnly;
      this[MUTATIONS.SET_SHOW_ONLY_SELECTED](true);
      await this.fetchData();
      this.isLoading = false;
    }
  }
}
</script>

<style scoped lang="scss">
  .c-grid-view {
    display: grid;

    &__container {
      display: grid;
      min-height: 280px;
      padding: 0;
      overflow-y: auto;
      grid-template-rows: 150px auto;
      -moz-column-gap: 25px;
      column-gap: 25px;
      row-gap: 5px;
      justify-self: center;
      grid-template-columns: repeat(6, minmax(100px, 1fr));

      @media (max-width: 1700px) {
        grid-template-columns: repeat(5, minmax(100px, 1fr)) !important;
      }

      @media (max-width: 1500px) {
        grid-template-columns: repeat(4, minmax(100px, 1fr)) !important;
      }

      @media (max-width: 1300px) {
        grid-template-columns: repeat(4, minmax(100px, 1fr)) !important;
      }

      @media (max-width: 1155px) {
        grid-template-columns: repeat(3, minmax(100px, 1fr)) !important;
      }

     @media (max-width: 991px) {
        grid-template-columns: repeat(2, minmax(100px, 1fr)) !important;
     }

      @media (max-width: 650px) {
        grid-template-columns: repeat(1, minmax(100px, 1fr)) !important;
      }
    }

    .folder-items {
      min-height: initial;
      row-gap: 14px;
      grid-template-rows: auto;
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
      padding: 0 8px;
    }
  }
</style>
